import { Immer } from 'immer';
import set from 'lodash/set';
import { scopeDataPath } from './brokenLinks/siteScopes';

/**
 * Écrire dans un bloc du site par son id, hors de Puck : la vue formulaire
 * édite des blocs de toutes les pages et du squelette, quand Puck ne connaît
 * que la portée affichée (`getItemById`).
 *
 * Tout renvoie la **même référence** quand rien ne change : ces écritures
 * partent aussi des flushers, rejoués à chaque instantané.
 */

const idOf = (block) => String(block?.props?.id ?? '');

/**
 * @param {object} puckData `{ content, zones }`
 * @param {(block: object) => object} update - renvoie le bloc tel quel s'il ne change pas
 */
export function updatePuckDataBlock(puckData, blockId, update) {
  if (!puckData) return puckData;
  const target = String(blockId);
  let found = false;
  const mapBlocks = (blocks) => {
    if (found || !Array.isArray(blocks)) return blocks;
    const index = blocks.findIndex((block) => idOf(block) === target);
    if (index < 0) return blocks;
    found = true;
    const updated = update(blocks[index]);
    if (updated === blocks[index]) return blocks;
    const next = blocks.slice();
    next[index] = updated;
    return next;
  };

  const content = mapBlocks(puckData.content);
  if (content !== puckData.content) return { ...puckData, content };
  if (found || !puckData.zones) return puckData;

  const entries = Object.entries(puckData.zones);
  const zoneEntries = entries.map(([key, blocks]) => [key, mapBlocks(blocks)]);
  const changed = zoneEntries.some(([, blocks], i) => blocks !== entries[i][1]);
  return changed ? { ...puckData, zones: Object.fromEntries(zoneEntries) } : puckData;
}

// Copie le long du chemin, et rend l'objet tel quel si la feuille ne change pas.
const updateIn = (container, [key, ...rest], update) => {
  const current = container?.[key];
  if (current === undefined) return container;
  const next = rest.length ? updateIn(current, rest, update) : update(current);
  return next === current ? container : { ...container, [key]: next };
};

/**
 * Même écriture sur le document entier, dans la portée nommée comme le font
 * `webEditorScopes` / `webTemplateScopes` : `page:<slug>`, ou la section du
 * squelette (`header`, `footer`…).
 *
 * @param {object} documentData - `webData` d'un site, instantané d'un modèle
 */
export function updateScopeBlock(documentData, scopeId, blockId, update, { template = false } = {}) {
  const path = scopeDataPath(scopeId, { template });
  if (!path) return documentData;
  return updateIn(documentData, path, (puckData) => updatePuckDataBlock(puckData, blockId, update));
}

// Sans gel : ces props repartent dans Puck, dont `setDeep` modifie ensuite les
// objets imbriqués au canvas.
const { produce } = new Immer({ autoFreeze: false });

/**
 * Les props avec la valeur posée à un chemin `setDeep` (`rows[0].cells[1].value`,
 * `backgroundMedia`). Les objets imbriqués sont partagés avec l'instantané et
 * les données de Puck : l'écriture se fait sur un brouillon immer. Pas de
 * `setDeep` ici : il copie la racine au lieu d'écrire dans le brouillon, et
 * perdrait un chemin d'un seul segment.
 */
export function setBlockPropAtPath(props, path, value) {
  return produce(props, (draft) => {
    set(draft, path, value);
  });
}
